import styled, { css } from "styled-components";

import { PriceNote } from "../../../../../components/Valuation";

import { FONTS, QUERIES, SPACING } from "../../../../../utils/constants";

const CARD_PADDING = SPACING.md;
const CARD_PADDING_WIDE = SPACING.lg;

export const Header = styled.header`
  position: relative;
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  background: var(--hero-background);
  box-shadow: var(--shadow-card-rest);
  font-family: ${FONTS.serif};
  padding: ${CARD_PADDING}px;

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${CARD_PADDING_WIDE}px;
  }
`;

export const Topline = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.lg}px;
  padding-right: ${SPACING.xl + SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    padding-right: 0;
  }
`;

export const Identity = styled.div`
  min-width: 0;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
`;

export const Name = styled.h1`
  margin: 0;
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-display);
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.1;
  overflow-wrap: anywhere;
`;

const badge = css`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
`;

const neutralBadge = css`
  border: 1px solid var(--color-card-border);
  background: color-mix(in srgb, var(--color-text-muted) 8%, transparent);
  color: var(--color-text-secondary);
`;

export const StatusBadge = styled.span<{ $isActive: boolean }>`
  ${badge}
  gap: ${SPACING.xs}px;
  ${({ $isActive }) =>
    $isActive
      ? css`
          background: var(--color-status-success-background);
          color: var(--color-status-success);
        `
      : neutralBadge}

  &::before {
    content: "";
    width: 0.4em;
    height: 0.4em;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

export const ModeBadge = styled.span`
  ${badge}
  ${neutralBadge}
`;

export const Subtitle = styled.p`
  margin: ${SPACING.sm}px 0 0;
  color: var(--color-text-secondary);
  font-size: var(--text-base);
`;

export const DesktopActions = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: ${SPACING.sm}px;
  }
`;

export const KebabWrapper = styled.div`
  position: absolute;
  top: ${SPACING.md}px;
  right: ${SPACING.md}px;
  z-index: 10;

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const SnapshotBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: ${CARD_PADDING}px;
  margin: ${CARD_PADDING}px -${CARD_PADDING}px 0;
  padding: ${CARD_PADDING}px ${CARD_PADDING}px 0;
  border-top: 1px solid var(--color-border);

  @media ${QUERIES.tabletLargeAndUp} {
    gap: ${CARD_PADDING_WIDE}px;
    margin: ${CARD_PADDING_WIDE}px -${CARD_PADDING_WIDE}px 0;
    padding: ${CARD_PADDING_WIDE}px ${CARD_PADDING_WIDE}px 0;
  }

  @media ${QUERIES.snapshotSplitAndUp} {
    grid-template-columns: minmax(360px, 1fr) minmax(280px, 0.7fr);
    gap: ${SPACING.xl}px;
  }
`;

export const Figures = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;

    > * + * {
      padding-left: ${SPACING.lg}px;
      border-left: 1px solid var(--color-border);
    }
  }
`;

export const Figure = styled.div`
  min-width: 0;
`;

export const FigureLabel = styled.span`
  display: block;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1.3;
  text-transform: uppercase;
`;

export const HeadlineAmount = styled.strong<{ $negative: boolean }>`
  display: block;
  margin-top: ${SPACING.xs}px;
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-text-default)"};
  font-family: ${FONTS.serif};
  font-size: var(--text-display-xl);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
`;

export const GainRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  margin-top: ${SPACING.xs}px;
`;

export const GainChip = styled.strong<{ $negative: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  font-family: ${FONTS.serif};
  font-size: var(--text-lg);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-success)"};

  svg {
    font-size: var(--text-lg);
  }
`;

const figureCaption = css`
  display: block;
  margin-top: ${SPACING.sm}px;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  line-height: 1.3;
`;

export const HeadlinePriceNote = styled(PriceNote)`
  ${figureCaption}
`;

export const FigureCaption = styled.small`
  ${figureCaption}
`;

export const SecondaryAmount = styled.strong`
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-lg);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
`;

export const Breakdown = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${SPACING.lg}px;
  margin: 0;
  padding-top: ${SPACING.md}px;
  border-top: 1px solid var(--color-border);

  @media ${QUERIES.snapshotSplitAndUp} {
    padding-top: 0;
    padding-left: ${SPACING.xl}px;
    border-top: 0;
    border-left: 1px solid var(--color-border);
  }
`;

export const BreakdownItem = styled.div`
  min-width: 0;

  dt {
    color: var(--color-text-secondary);
    font-size: var(--text-sm);
  }

  dd {
    margin: ${SPACING.xs}px 0 0;
    color: var(--color-text-default);
    font-size: var(--text-base);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
  }

  dd + dd {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 400;
    line-height: 1.3;
  }
`;
